// covered-by: frontends/carrie/src/ask-v2/pages/__tests__/AskV2ProjectPage.test.tsx
import { type AskPunttMessage } from "@mg/schemas/src/christo/askPuntt";
import { useMemo } from "react";

import { type useAskV2ReviewWorkspace } from "./useAskV2ReviewWorkspace";

import { type AskPunttArtifactContextValue } from "../../components/ask-puntt-review";

function collectReviewIds(messages: readonly AskPunttMessage[]): string[] {
  const ids: string[] = [];
  const seen = new Set<string>();
  for (const message of messages) {
    const reviewId = message.viewReviewId;
    if (!reviewId || seen.has(reviewId)) continue;
    seen.add(reviewId);
    ids.push(reviewId);
  }
  return ids;
}

function isArtifactStreaming(messages: readonly AskPunttMessage[]): boolean {
  for (let index = messages.length - 1; index >= 0; index -= 1) {
    const message = messages[index];
    if (message.role !== "assistant") continue;
    if (message.status !== "pending") return false;
    return Boolean(message.viewReviewId || message.canvasLoading);
  }
  return false;
}

export function useAskV2ReviewArtifactContext(options: {
  workspace: ReturnType<typeof useAskV2ReviewWorkspace>;
  messages: readonly AskPunttMessage[];
  unseenArtifactKeys?: ReadonlySet<string>;
  markReviewSeen?: (reviewId: string) => void;
}): AskPunttArtifactContextValue {
  const { workspace, messages, unseenArtifactKeys, markReviewSeen } = options;
  const allReviewIds = useMemo(() => collectReviewIds(messages), [messages]);
  const canvasMessageIds = useMemo(
    () =>
      messages
        .filter(
          (message) =>
            message.role === "assistant" &&
            Array.isArray(message.canvasContent?.blocks) &&
            message.canvasContent.blocks.length > 0,
        )
        .map((message) => message._id),
    [messages],
  );

  return useMemo(
    () => ({
      activeTicketId: null,
      activeReviewId: workspace.reviewId,
      activeCanvasMessageId: null,
      onActivateTicket: () => {},
      onActivateReview: workspace.openReview,
      onActivateCanvas: () => {},
      isAnotherArtifactStreaming: isArtifactStreaming(messages),
      allReviewTicketIds: [],
      allReviewIds,
      canvasMessageIds,
      openArtifact: (payload) => {
        if (payload.kind === "askPunttReview") {
          workspace.openReview(payload.reviewId);
        }
      },
      closeArtifact: workspace.closeReview,
      unseenArtifactKeys,
      markReviewSeen,
    }),
    [
      allReviewIds,
      canvasMessageIds,
      markReviewSeen,
      messages,
      unseenArtifactKeys,
      workspace,
    ],
  );
}
